import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fechaHora, fechaSolo, money } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Inbox, Check, X, Trash2, Footprints, PackageOpen } from "lucide-react";

import FechaInput from "@/components/FechaInput";
import RutaHormaCampos, { useRutas, rutaListo } from "@/components/RutaHormaCampos";
const ESTADO_BADGE = {
  pendiente: "bg-amber-100 text-amber-800",
  autorizada: "bg-emerald-100 text-emerald-800",
  rechazada: "bg-red-100 text-red-700",
};
const ESTADO_LABEL = { pendiente: "Pendiente", autorizada: "Autorizada", rechazada: "Rechazada" };

const emptyModel = {
  nombre: "", sku: "",
  ruta: "", horma_id: "",
  material: "", suela: "",
  categoria: "", genero: "", estrategia: "", ciclo_vida: "", linea: "", proveedor: "",
  costo_meta: "", drop: "", compra_inicial: "",
  fecha_meta_prototipo: "", notas_desarrollo: "",
};
const mkProducto = () => ({ nombre: "", sku: "", color: "", talla: "", cantidad: 1, precio: "", _key: crypto.randomUUID() });

export default function Solicitudes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modelItems, setModelItems] = useState(null);
  const [muestraItems, setMuestraItems] = useState(null);
  const [listas, setListas] = useState({});
  const [colecciones, setColecciones] = useState([]);
  const [busy, setBusy] = useState(false);

  // Formularios
  const [openModel, setOpenModel] = useState(false);
  const [form, setForm] = useState(emptyModel);
  const [openMuestra, setOpenMuestra] = useState(false);
  const [muestra, setMuestra] = useState({ cliente_nombre: "", fecha_requerida: "", comentario: "", productos: [mkProducto()] });

  // Autorizar / rechazar
  const [autorizar, setAutorizar] = useState(null); // {tipo, item}
  const [colElegida, setColElegida] = useState("__muestras__");
  const [rutaAut, setRutaAut] = useState({ ruta: "", horma_id: "" }); // quien autoriza puede corregir la ruta
  const rutas = useRutas();
  const conPrototipo = (rid) => !rid || rutas.find((r) => r.id === rid)?.con_prototipo !== false;
  const [rechazar, setRechazar] = useState(null); // {tipo, item}
  const [motivo, setMotivo] = useState("");

  const puedeModelo = user?.es_admin || user?.perms?.acciones?.crear_solicitudes;
  const puedeMuestra = user?.es_admin || user?.perms?.acciones?.crear_muestras;
  const puedeAutorizar = user?.es_admin || user?.perms?.acciones?.autorizar_solicitudes;

  const load = () => {
    api.get("/solicitudes").then((r) => setModelItems(r.data)).catch(() => setModelItems([]));
    api.get("/solicitudes-muestra").then((r) => setMuestraItems(r.data)).catch(() => setMuestraItems([]));
    window.dispatchEvent(new Event("solicitudes-changed"));
  };
  useEffect(() => {
    load();
    api.get("/listas").then((r) => setListas(r.data));
    api.get("/colecciones").then((r) => setColecciones(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const coleccionesActivas = useMemo(() => colecciones.filter((c) => c.estado !== "completada"), [colecciones]);

  const combinados = useMemo(() => {
    const a = (modelItems || []).map((m) => ({ tipo: "modelo", raw: m, id: m.id, titulo: m.nombre, sub: `${m.organizacion?.categoria || "—"} · ${m.organizacion?.genero || "—"}`, estado: m.estado_solicitud, solicitante: m.solicitante_nombre, fecha: m.created_at, motivo: m.motivo_rechazo }));
    const b = (muestraItems || []).map((m) => ({ tipo: "muestra", raw: m, id: m.id, titulo: m.folio, sub: `${m.cliente_nombre} · ${m.productos?.length || 0} producto(s)`, estado: m.estado_solicitud, solicitante: m.solicitante_nombre, fecha: m.created_at, motivo: m.motivo_rechazo }));
    return [...a, ...b].sort((x, y) => (y.fecha || "").localeCompare(x.fecha || ""));
  }, [modelItems, muestraItems]);


  const crearModelo = async () => {
    setBusy(true);
    try {
      await api.post("/solicitudes", {
        nombre: form.nombre, sku: form.sku,
        ruta: form.ruta, horma_id: form.horma_id || null,
        ficha_tecnica: { material: form.material, suela: form.suela },
        organizacion: { categoria: form.categoria, genero: form.genero, estrategia: form.estrategia, ciclo_vida: form.ciclo_vida, linea: form.linea, proveedor: form.proveedor },
        comercial: { costo_meta: form.costo_meta === "" ? null : Number(form.costo_meta), drop: form.drop, compra_inicial: form.compra_inicial === "" ? null : Number(form.compra_inicial) },
        notas_desarrollo: form.notas_desarrollo,
        fecha_meta_prototipo: conPrototipo(form.ruta) ? form.fecha_meta_prototipo || null : null,
      });
      toast.success("Solicitud de modelo enviada");
      setOpenModel(false); setForm(emptyModel); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const crearMuestra = async () => {
    setBusy(true);
    try {
      await api.post("/solicitudes-muestra", {
        cliente_nombre: muestra.cliente_nombre,
        fecha_requerida: muestra.fecha_requerida || null,
        comentario_creacion: (muestra.comentario || "").trim() || null,
        productos: muestra.productos.filter((p) => p.nombre.trim()).map((p) => ({
          nombre: p.nombre, sku: p.sku, color: p.color, talla: p.talla,
          cantidad: Number(p.cantidad) || 1, precio: p.precio === "" ? null : Number(p.precio),
        })),
      });
      toast.success("Solicitud de muestra enviada");
      setOpenMuestra(false);
      setMuestra({ cliente_nombre: "", fecha_requerida: "", comentario: "", productos: [mkProducto()] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const confirmarAutorizar = async () => {
    setBusy(true);
    try {
      if (autorizar.tipo === "modelo") {
        const coleccion_id = colElegida === "__muestras__" ? null : colElegida;
        const cambio = rutaAut.ruta && rutaAut.ruta !== autorizar.raw?.ruta_info?.id;
        await api.post(`/solicitudes/${autorizar.id}/autorizar`, { coleccion_id, ...(cambio ? { ruta: rutaAut.ruta, horma_id: rutaAut.horma_id || null } : {}) });
      } else {
        await api.post(`/solicitudes-muestra/${autorizar.id}/autorizar`);
      }
      toast.success("Solicitud autorizada");
      setAutorizar(null); setColElegida("__muestras__"); load();
      api.get("/colecciones").then((r) => setColecciones(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const confirmarRechazar = async () => {
    setBusy(true);
    try {
      const base = rechazar.tipo === "modelo" ? "/solicitudes" : "/solicitudes-muestra";
      await api.post(`${base}/${rechazar.id}/rechazar`, { motivo });
      toast.success("Solicitud rechazada");
      setRechazar(null); setMotivo(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const setProd = (i, campo, val) => setMuestra((m) => ({ ...m, productos: m.productos.map((p, idx) => idx === i ? { ...p, [campo]: val } : p) }));
  const addProd = () => setMuestra((m) => ({ ...m, productos: [...m.productos, mkProducto()] }));
  const delProd = (i) => setMuestra((m) => ({ ...m, productos: m.productos.filter((_, idx) => idx !== i) }));

  const Drop = ({ campo, label }) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={form[campo]} onValueChange={(v) => setForm({ ...form, [campo]: v })}>
        <SelectTrigger data-testid={`sol-${campo}-input`}><SelectValue placeholder="Selecciona" /></SelectTrigger>
        <SelectContent>{(listas[campo]?.valores || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  const cargando = modelItems === null || muestraItems === null;

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-slate-900 text-white flex items-center justify-center"><Inbox size={18} /></div>
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Solicitudes</h1>
          </div>
          <p className="text-slate-500 mt-1">Solicita un nuevo modelo o un pedido de muestra. Todo pasa por autorización.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.es_admin && (
            <Button variant="ghost" className="rounded-lg text-slate-500" onClick={() => navigate("/solicitudes/eliminadas")} data-testid="ver-eliminadas-button">
              <Trash2 size={16} className="mr-1.5" /> Eliminadas
            </Button>
          )}
          {puedeModelo && (
            <Button variant="outline" className="rounded-lg" onClick={() => { setForm(emptyModel); setOpenModel(true); }} data-testid="new-solicitud-button">
              <Footprints size={16} className="mr-1.5" /> Nuevo modelo
            </Button>
          )}
          {puedeMuestra && (
            <Button className="rounded-lg" onClick={() => { setMuestra({ cliente_nombre: "", fecha_requerida: "", comentario: "", productos: [mkProducto()] }); setOpenMuestra(true); }} data-testid="new-muestra-button">
              <PackageOpen size={16} className="mr-1.5" /> Nueva muestra
            </Button>
          )}
        </div>
      </div>

      {cargando ? (
        <Skeleton className="h-72 rounded-xl" />
      ) : combinados.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-dashed">No hay solicitudes todavía.</Card>
      ) : (
        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-3 font-semibold">Tipo</th>
                  <th className="px-4 py-3 font-semibold">Solicitud</th>
                  <th className="px-4 py-3 font-semibold">Solicitante</th>
                  <th className="px-4 py-3 font-semibold">Fecha</th>
                  <th className="px-4 py-3 font-semibold">Estado</th>
                  {puedeAutorizar && <th className="px-4 py-3 font-semibold text-right">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {combinados.map((s) => (
                  <tr key={`${s.tipo}-${s.id}`} onClick={s.tipo === "muestra" ? () => navigate(`/solicitudes/muestra/${s.id}`) : undefined} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${s.tipo === "muestra" ? "cursor-pointer" : ""}`} data-testid={`solicitud-row-${s.id}`}>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold ${s.tipo === "muestra" ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"}`}>
                        {s.tipo === "muestra" ? <PackageOpen size={11} /> : <Footprints size={11} />}
                        {s.tipo === "muestra" ? "Muestra" : "Modelo"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {s.tipo === "muestra" ? (
                        <span className="font-semibold text-slate-800 hover:text-blue-600" data-testid={`ver-muestra-${s.id}`}>{s.titulo}</span>
                      ) : (
                        <div className="font-semibold text-slate-800">{s.titulo}</div>
                      )}
                      <div className="text-xs text-slate-400">{s.sub}</div>
                      {s.estado === "rechazada" && s.motivo && <div className="text-[11px] text-red-500 mt-0.5">Motivo: {s.motivo}</div>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{s.solicitante}</td>
                    <td className="px-4 py-3 text-slate-500">{fechaHora(s.fecha)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${ESTADO_BADGE[s.estado] || "bg-slate-100 text-slate-600"}`} data-testid={`solicitud-estado-${s.id}`}>
                        {ESTADO_LABEL[s.estado] || s.estado}
                      </span>
                    </td>
                    {puedeAutorizar && (
                      <td className="px-4 py-3 text-right">
                        {s.estado === "pendiente" ? (
                          <div className="flex items-center gap-1.5 justify-end">
                            <Button size="sm" className="h-8" onClick={(e) => { e.stopPropagation(); setAutorizar({ tipo: s.tipo, id: s.id, raw: s.raw }); setColElegida("__muestras__"); setRutaAut({ ruta: s.raw?.ruta_info?.id || "", horma_id: s.raw?.horma?.id || "" }); }} data-testid={`autorizar-${s.id}`}>
                              <Check size={14} className="mr-1" /> Autorizar
                            </Button>
                            <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); setRechazar({ tipo: s.tipo, id: s.id }); setMotivo(""); }} data-testid={`rechazar-${s.id}`}>
                              <X size={14} className="mr-1" /> Rechazar
                            </Button>
                          </div>
                        ) : <span className="text-xs text-slate-300">—</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Nueva solicitud de modelo */}
      <Dialog open={openModel} onOpenChange={setOpenModel}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto thin-scroll">
          <DialogHeader><DialogTitle>Nueva solicitud de modelo</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Identidad</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Nombre del modelo *</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="sol-nombre-input" /></div>
                <div className="space-y-1.5"><Label>SKU <span className="text-slate-400 font-normal">(opcional)</span></Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="sol-sku-input" /></div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Ruta de desarrollo</p>
              <RutaHormaCampos value={{ ruta: form.ruta, horma_id: form.horma_id }} onChange={(v) => setForm({ ...form, ...v })} testid="sol-ruta" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Ficha técnica</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Material (upper)</Label><Input value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} data-testid="sol-material-input" /></div>
                <div className="space-y-1.5"><Label>Suela</Label><Input value={form.suela} onChange={(e) => setForm({ ...form, suela: e.target.value })} data-testid="sol-suela-input" /></div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Organización del producto</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Drop campo="categoria" label="Categoría" />
                <Drop campo="genero" label="Género" />
                <Drop campo="estrategia" label="Estrategia" />
                <Drop campo="ciclo_vida" label="Ciclo de vida" />
                <div className="space-y-1.5"><Label>Línea</Label><Input value={form.linea} onChange={(e) => setForm({ ...form, linea: e.target.value })} data-testid="sol-linea-input" /></div>
                <div className="space-y-1.5"><Label>Proveedor / maquilador</Label><Input value={form.proveedor} onChange={(e) => setForm({ ...form, proveedor: e.target.value })} data-testid="sol-proveedor-input" /></div>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Comercial y planeación</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Costo meta (MXN)</Label><Input type="number" value={form.costo_meta} onChange={(e) => setForm({ ...form, costo_meta: e.target.value })} data-testid="sol-costo-input" /></div>
                <div className="space-y-1.5"><Label>Drop</Label><Input value={form.drop} onChange={(e) => setForm({ ...form, drop: e.target.value })} data-testid="sol-drop-input" /></div>
                {conPrototipo(form.ruta) && <div className="space-y-1.5"><Label>Fecha meta prototipo</Label><FechaInput value={form.fecha_meta_prototipo} onChange={(e) => setForm({ ...form, fecha_meta_prototipo: e.target.value })} data-testid="sol-meta-input" /></div>}
              </div>
              <div className="space-y-1.5 mt-3"><Label>Notas de desarrollo</Label><Textarea rows={3} value={form.notas_desarrollo} onChange={(e) => setForm({ ...form, notas_desarrollo: e.target.value })} data-testid="sol-notas-input" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={crearModelo} disabled={busy || !form.nombre.trim() || !rutaListo(rutas, form)} data-testid="sol-save-button">Enviar solicitud</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nueva solicitud de muestra (pedido) */}
      <Dialog open={openMuestra} onOpenChange={setOpenMuestra}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto thin-scroll">
          <DialogHeader><DialogTitle>Nueva solicitud de muestra</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500">La numeración (folio) se asigna automáticamente al enviar.</div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Datos del cliente</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Nombre del cliente *</Label><Input value={muestra.cliente_nombre} onChange={(e) => setMuestra({ ...muestra, cliente_nombre: e.target.value })} data-testid="mue-cliente-input" /></div>
                <div className="space-y-1.5"><Label>Fecha requerida</Label><FechaInput value={muestra.fecha_requerida} onChange={(e) => setMuestra({ ...muestra, fecha_requerida: e.target.value })} data-testid="mue-fecha-input" /></div>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Productos</p>
                <Button type="button" size="sm" variant="outline" className="h-7" onClick={addProd} data-testid="mue-add-producto"><Plus size={14} className="mr-1" /> Agregar producto</Button>
              </div>
              <div className="space-y-2">
                {muestra.productos.map((p, i) => (
                  <div key={p._key} className="grid grid-cols-12 gap-2 items-end" data-testid={`mue-producto-${i}`}>
                    <div className="col-span-2 space-y-1"><Label className="text-[11px]">SKU</Label><Input value={p.sku} onChange={(e) => setProd(i, "sku", e.target.value)} placeholder="opc." data-testid={`mue-prod-sku-${i}`} /></div>
                    <div className="col-span-3 space-y-1"><Label className="text-[11px]">Producto</Label><Input value={p.nombre} onChange={(e) => setProd(i, "nombre", e.target.value)} data-testid={`mue-prod-nombre-${i}`} /></div>
                    <div className="col-span-2 space-y-1"><Label className="text-[11px]">Color</Label><Input value={p.color} onChange={(e) => setProd(i, "color", e.target.value)} data-testid={`mue-prod-color-${i}`} /></div>
                    <div className="col-span-1 space-y-1"><Label className="text-[11px]">Talla</Label><Input value={p.talla} onChange={(e) => setProd(i, "talla", e.target.value)} data-testid={`mue-prod-talla-${i}`} /></div>
                    <div className="col-span-1 space-y-1"><Label className="text-[11px]">Cant.</Label><Input type="number" value={p.cantidad} onChange={(e) => setProd(i, "cantidad", e.target.value)} data-testid={`mue-prod-cantidad-${i}`} /></div>
                    <div className="col-span-2 space-y-1"><Label className="text-[11px]">Precio (MXN)</Label><Input type="number" value={p.precio} onChange={(e) => setProd(i, "precio", e.target.value)} placeholder="opc." data-testid={`mue-prod-precio-${i}`} /></div>
                    <div className="col-span-1 flex justify-center pb-1">
                      {muestra.productos.length > 1 && <button type="button" onClick={() => delProd(i)} className="text-slate-400 hover:text-red-600" data-testid={`mue-del-producto-${i}`}><Trash2 size={16} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Comentarios <span className="text-slate-400 font-normal">(opcional)</span></Label>
              <Textarea rows={3} value={muestra.comentario || ""} onChange={(e) => setMuestra({ ...muestra, comentario: e.target.value })} placeholder="Notas o instrucciones para esta solicitud…" data-testid="mue-comentario-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={crearMuestra} disabled={busy || !muestra.cliente_nombre.trim() || !muestra.productos.some((p) => p.nombre.trim())} data-testid="mue-save-button">Enviar solicitud</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Autorizar */}
      <Dialog open={!!autorizar} onOpenChange={(o) => !o && setAutorizar(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Autorizar solicitud</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {autorizar?.tipo === "modelo" ? (
              <>
                <p className="text-sm text-slate-600">Asigna <span className="font-semibold">{autorizar?.raw?.nombre}</span> a una colección activa.</p>
                <div className="space-y-1.5">
                  <Label>Colección</Label>
                  <Select value={colElegida} onValueChange={setColElegida}>
                    <SelectTrigger data-testid="autorizar-coleccion-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__muestras__">Colección "Muestras" (por defecto, sin fechas)</SelectItem>
                      {coleccionesActivas.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs text-slate-500">Revisa la ruta que eligió el solicitante; si no es la correcta, corrígela antes de autorizar.</p>
                  <RutaHormaCampos value={rutaAut} onChange={setRutaAut} testid="autorizar-ruta" />
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-600">Se autorizará el pedido de muestra <span className="font-semibold">{autorizar?.raw?.folio}</span> y aparecerá en el menú Muestras.</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={confirmarAutorizar} disabled={busy || (autorizar?.tipo === "modelo" && !rutaListo(rutas, rutaAut))} data-testid="autorizar-confirm">Autorizar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rechazar */}
      <Dialog open={!!rechazar} onOpenChange={(o) => !o && setRechazar(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rechazar solicitud</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">Se notificará por correo al solicitante.</p>
            <div className="space-y-1.5"><Label>Motivo {rechazar?.tipo === "muestra" ? <span className="text-red-500">*</span> : <span className="text-slate-400 font-normal">(opcional)</span>}</Label><Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder={rechazar?.tipo === "muestra" ? "Explica por qué se rechaza esta solicitud…" : ""} data-testid="rechazar-motivo" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={confirmarRechazar} disabled={busy || (rechazar?.tipo === "muestra" && !motivo.trim())} data-testid="rechazar-confirm">Rechazar solicitud</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
